import React, { FC, useEffect, useMemo, useState } from 'react';
import { appSettings } from 'AppSettings';
import { GetMigrations, Migration } from 'data/MigrationsData';
import { orderBy } from 'lodash';
import { Page } from 'components/Page';
import useAuth from 'auth/UseAuth';
import { AuthorizedPage } from 'auth/AuthorizedPage';
import { AuthorizedSection } from 'auth/AuthorizedSection';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import useMemoTranslation from '../hooks/UseMemoTranslation';
import { description, installedOn, version } from '../util/Constants';
import { CompactGridWrapper } from '../components/grid/CompactGridWrapper';
import { PageContent } from '../components/PageContent';
import { dateGetter } from '../util/grid/TableUtils';
import { FlexBox } from '../components/FlexBox';
import { FlexTableBox } from '../components/FlexTableBox';
import { Box, Divider, Typography } from '@mui/material';
import { ErrorManagement, LoadingState, LoadState } from '../components/LoadingStateUtil';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';

export const MigrationsPage: FC = () => {
  const { t } = useMemoTranslation();
  const [migrations, setMigrations] = useState<Migration[]>([]);
  const [tbdMigrations, setTbdMigrations] = useState<Migration[]>([]);
  const [doneMigrations, setDoneMigrations] = useState<Migration[]>([]);
  const [apiCallCount, refreshMigrations] = useState<number>(0);
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });

  const { accessToken } = useAuth();

  useEffect(() => {
    return LoadState(setLoadingState, async () => {
      if (!accessToken) {
        return;
      }

      setMigrations(await GetMigrations(accessToken));
    });
  }, [accessToken, apiCallCount]);

  useEffect(() => {
    setTbdMigrations(
      migrations
        .filter(m => m.category === 'Pending')
        .map((i, index) => {
          return { ...i, id: index };
        })
    );
    const allDoneMigrations = [
      ...orderBy(
        migrations.filter(m => m.category !== 'Pending'),
        ['installedOn'],
        ['desc']
      ),
    ];

    const filtered: Migration[] = [];
    const seenVersions: { [ver: string]: boolean } = {};

    for (const m of allDoneMigrations) {
      if (!(m.version in seenVersions)) {
        seenVersions[m.version] = true;
        filtered.push(m);
      }
    }

    setDoneMigrations(
      filtered.map((i, index) => {
        return { ...i, id: index };
      })
    );
  }, [migrations]);

  function repairMigrations() {
    setLoadingState({ status: 'Loading' });
    ErrorManagement('Loading', setLoadingState, async () => {
      setLoadingState({ status: 'Loading' });
      const response = await fetch(`${appSettings.api.endpoint}/api/v2/Migration/Repair`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 200) {
        alert('Migration repair executed successfully.');
      } else {
        alert(`Migration repair failed: ${response.status} - ${response.statusText}`);
      }

      refreshMigrations(apiCallCount + 1);
    });
  }

  function runMigrations() {
    ErrorManagement('Loading', setLoadingState, async () => {
      setLoadingState({ status: 'Loading' });
      const response = await fetch(`${appSettings.api.endpoint}/api/v2/Migration`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 200) {
        alert('Migrations executed successfully.');
      } else {
        const msg = (await response.json()).title;
        alert(`Migrations failed: ${response.status} - ${response.statusText}\n\n${msg}`);
      }

      refreshMigrations(apiCallCount + 1);
    });
  }

  return (
    <AuthorizedPage hasAdminAccess>
      <Page title='Living Lab Migrations'>
        <PageContent>
          <Box sx={{ height: '100%', overflow: 'hidden' }} mb={2}>
            <FlexBox sx={{ flexDirection: 'row', gap: 3, height: '100%' }}>
              <FlexBox sx={{ flexDirection: 'column', gap: 2, width: '40%' }}>
                <Typography variant={'h3'}>{t('pending')}</Typography>
                <FlexTableBox>
                  <Table
                    loadingState={loadingState}
                    migrations={tbdMigrations}
                    initialState={{
                      columns: {
                        columnVisibilityModel: {
                          installedOn: false,
                        },
                      },
                    }}
                  />
                </FlexTableBox>
                <div style={{ paddingTop: '5px' }}>
                  <AuthorizedSection hasAdminAccess>
                    <button style={{ backgroundColor: 'lightgreen' }} onClick={runMigrations}>
                      Apply Migrations
                    </button>
                    <span style={{ paddingRight: '50px' }}></span>
                    <button style={{ backgroundColor: 'pink' }} onClick={repairMigrations}>
                      Repair Migrations
                    </button>
                  </AuthorizedSection>
                </div>
              </FlexBox>

              <Divider orientation={'vertical'} />

              <FlexBox sx={{ flexDirection: 'column', gap: 2, width: '60%' }}>
                <Typography variant={'h3'}>{t('completed')}</Typography>
                <Table
                  loadingState={loadingState}
                  migrations={doneMigrations}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: installedOn, sort: 'desc' }],
                    },
                  }}
                />
              </FlexBox>
            </FlexBox>
          </Box>
        </PageContent>
      </Page>
    </AuthorizedPage>
  );
};

const Table = ({
  migrations,
  initialState,
  loadingState,
}: {
  migrations: ReadonlyArray<Migration>;
  initialState?: GridInitialStatePro;
  loadingState: LoadingState;
}) => {
  const apiRef = useGridApiRef();
  const columns = useColumns();

  return (
    <CompactGridWrapper
      apiRef={apiRef}
      loadingState={loadingState}
      columns={columns}
      rows={migrations}
      columnsToAutoSize={columns.map(c => c.field)}
      disableRowSelectionOnClick={true}
      initialState={initialState}
    />
  );
};

const useColumns = (): GridColDef[] => {
  const { t } = useMemoTranslation();

  return useMemo(
    () => [
      {
        field: version,
        headerName: t(version),
        align: 'left',
        maxWidth: 120,
      },
      {
        field: description,
        headerName: t(description),
        align: 'left',
      },
      {
        field: installedOn,
        headerName: t(installedOn),
        align: 'left',
        type: 'date',
        valueGetter: dateGetter,
      },
    ],
    [t]
  );
};
